import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Avatar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Cake as CakeIcon,
  Favorite as FavoriteIcon,
  LocalHospital as LocalHospitalIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Notification from "../../components/common/Notification";
import { useAppSelector } from "../../redux/hooks";
import Loader from "../../components/common/Loader";
import {
  updateDonorApplication,
  createDonorApplication,
  resetDonorApplicationState,
  getDonorApplicationById,
} from "../../redux/slices/donorApplication";

const StyledFormContainer = styled(Box)({
  width: "100%",
  height: "100%",
  backgroundColor: "#f8fafc",
  padding: "24px",
  minHeight: "100vh",
});

const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  marginBottom: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #3b82f6, #1d4ed8)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused": {
      backgroundColor: "#ffffff",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#64748b",
    fontWeight: 500,
    "&.Mui-focused": {
      color: "#3b82f6",
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: "12px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#f1f5f9",
    borderColor: "#cbd5e1",
  },
  "&.Mui-focused": {
    backgroundColor: "#ffffff",
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 32px",
  fontSize: "16px",
  fontFamily: "Poppins, sans-serif",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
  "&.MuiButton-contained": {
    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    color: "#ffffff",
    "&:hover": {
      background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
    },
  },
  "&.MuiButton-outlined": {
    borderColor: "#e2e8f0",
    color: "#64748b",
    backgroundColor: "#ffffff",
    "&:hover": {
      borderColor: "#cbd5e1",
      backgroundColor: "#f8fafc",
    },
  },
}));

const StyledTypography = styled(Typography)({
  fontFamily: "Poppins, sans-serif !important",
});

const DonorApplicationForm = ({ onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { loading, error, success, currentDonorApplication } = useAppSelector(
    (state) => state.donorApplication
  );

  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: "",
    dateOfBirth: "",
    gender: "",
    bloodGroup: "",
    mobileNumber: "",
    emailId: "",
    address: "",

    // Organ Donation
    organs: {
      allOrgans: false,
      liver: false,
      kidney: false,
      heart: false,
      lungs: false,
      pancreas: false,
      intestine: false,
    },

    // Tissue Donation
    tissues: {
      allTissues: false,
      cornea: false,
      skin: false,
      bone: false,
      heartValve: false,
      bloodVessels: false,
      cartilage: false,
    },

    // Emergency Contact
    emergencyContact: {
      name: "",
      relation: "",
      mobile: "",
    },

    // Member to Notify
    memberToNotify: {
      sameAsEmergency: false,
      name: "",
      relation: "",
      mobile: "",
    },

    status: "",

    // Motivation
    motivation: {
      type: "NGO",
      ngoName: "Jeevandeep Organ Foundation",
    },

    // Authorization
    authorized: false,
  });

  const [memberSameAsEmergency, setMemberSameAsEmergency] = useState(false);

  useEffect(() => {
    if (id && !currentDonorApplication) {
      dispatch(getDonorApplicationById(id))
        .unwrap()
        .catch((error) => {
          setNotification({
            type: "error",
            message: "Failed to fetch donor application details",
          });
        });
    } else if (!id && currentDonorApplication) {
      dispatch(resetDonorApplicationState());
    }
  }, [id, currentDonorApplication, dispatch]);

  useEffect(() => {
    if (success) {
      setNotification({
        type: "success",
        message: currentDonorApplication
          ? "Donor Application updated successfully!"
          : "Donor Application created successfully!",
      });
      setTimeout(() => {
        setNotification(null);
        dispatch(resetDonorApplicationState());
        navigate("/donor-application", { replace: true });
      }, 1500);
    }
  }, [success, currentDonorApplication, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      setNotification({
        type: "error",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Something went wrong!",
      });
    }
  }, [error]);

  useEffect(() => {
    if (currentDonorApplication) {
      setFormData({
        fullName: currentDonorApplication.fullName || "",
        dateOfBirth: currentDonorApplication.dateOfBirth
          ? new Date(currentDonorApplication.dateOfBirth)
              .toISOString()
              .split("T")[0]
          : "",
        gender: currentDonorApplication.gender || "",
        bloodGroup: currentDonorApplication.bloodGroup || "",
        mobileNumber: currentDonorApplication.mobileNumber || "",
        emailId: currentDonorApplication.emailId || "",
        address: currentDonorApplication.address || "",
        status: currentDonorApplication.status || "",

        organs: {
          allOrgans: currentDonorApplication.organs?.allOrgans || false,
          liver: currentDonorApplication.organs?.liver || false,
          kidney: currentDonorApplication.organs?.kidney || false,
          heart: currentDonorApplication.organs?.heart || false,
          lungs: currentDonorApplication.organs?.lungs || false,
          pancreas: currentDonorApplication.organs?.pancreas || false,
          intestine: currentDonorApplication.organs?.intestine || false,
        },

        tissues: {
          allTissues: currentDonorApplication.tissues?.allTissues || false,
          cornea: currentDonorApplication.tissues?.cornea || false,
          skin: currentDonorApplication.tissues?.skin || false,
          bone: currentDonorApplication.tissues?.bone || false,
          heartValve: currentDonorApplication.tissues?.heartValve || false,
          bloodVessels: currentDonorApplication.tissues?.bloodVessels || false,
          cartilage: currentDonorApplication.tissues?.cartilage || false,
        },

        emergencyContact: {
          name: currentDonorApplication.emergencyContact?.name || "",
          relation: currentDonorApplication.emergencyContact?.relation || "",
          mobile: currentDonorApplication.emergencyContact?.mobile || "",
          email: currentDonorApplication.emergencyContact?.email || "",
        },

        memberToNotify: currentDonorApplication.memberToNotify || {
          sameAsEmergency: false,
          name: "",
          relation: "",
          mobile: "",
          email: "",
        },

        motivation: currentDonorApplication.motivation || {
          type: "NGO",
          ngoName: "Jeevandeep Organ Foundation",
        },

        authorized: currentDonorApplication.authorized || false,
      });

      setMemberSameAsEmergency(
        currentDonorApplication.memberToNotify?.sameAsEmergency || false
      );
    }
  }, [currentDonorApplication]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      // Handle nested organ/tissue checkboxes
      if (name.startsWith("organs.")) {
        const field = name.split(".")[1];
        setFormData((prev) => ({
          ...prev,
          organs: {
            ...prev.organs,
            [field]: checked,
          },
        }));
      } else if (name.startsWith("tissues.")) {
        const field = name.split(".")[1];
        setFormData((prev) => ({
          ...prev,
          tissues: {
            ...prev.tissues,
            [field]: checked,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else if (name.startsWith("emergencyContact.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          [field]: value,
        },
      }));
    } else if (name.startsWith("memberToNotify.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        memberToNotify: {
          ...prev.memberToNotify,
          [field]: value,
        },
      }));
    } else if (name === "dateOfBirth") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification(null);

    try {
      if (!formData.fullName.trim()) {
        setNotification({
          type: "error",
          message: "Full name is required",
        });
        return;
      }

      if (!formData.mobileNumber.trim()) {
        setNotification({
          type: "error",
          message: "Mobile number is required",
        });
        return;
      }

      if (!formData.authorized) {
        setNotification({
          type: "error",
          message: "Authorization is required",
        });
        return;
      }

      // Prepare memberToNotify data
      const memberToNotifyData = memberSameAsEmergency
        ? {
            sameAsEmergency: true,
            name: formData.emergencyContact.name,
            relation: formData.emergencyContact.relation,
            mobile: formData.emergencyContact.mobile,
            email: formData.emergencyContact.email,
          }
        : formData.memberToNotify;

      const submissionData = {
        ...formData,
        memberToNotify: memberToNotifyData,
      };

      if (currentDonorApplication) {
        await dispatch(
          updateDonorApplication({
            id: currentDonorApplication._id,
            data: submissionData,
          })
        ).unwrap();
      } else {
        await dispatch(createDonorApplication(submissionData)).unwrap();
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setNotification({
        type: "error",
        message: err?.message || "Operation failed. Please try again.",
      });
    }
  };

  const handleCancel = () => {
    navigate("/donor-application");
  };

  return (
    <StyledFormContainer className="poppins-font">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
        className="poppins-font"
      >
        <IconButton
          onClick={handleCancel}
          sx={{
            mr: 2,
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <StyledTypography variant="h5" component="h1" fontWeight="bold">
          {currentDonorApplication ? "Edit" : "Add New"} Donor Application
        </StyledTypography>
      </Box>

      {/* Main Form Content */}
      <Box sx={{ p: 3 }}>
        <form onSubmit={handleSubmit} className="poppins-font">
          {/* Personal Information Section */}
          <StyledCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                <PersonIcon />
              </Avatar>
              <Typography
                variant="h6"
                className="poppins-font"
                sx={{ fontWeight: 600 }}
              >
                Personal Information
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Full Name *"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StyledTextField
                  fullWidth
                  label="Date of Birth *"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Gender *</InputLabel>
                  <StyledSelect
                    name="gender"
                    value={formData.gender}
                    label="Gender *"
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </StyledSelect>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Blood Group</InputLabel>
                  <StyledSelect
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    label="Blood Group"
                    onChange={handleChange}
                  >
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </StyledSelect>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Mobile Number *"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  required
                  placeholder="Enter mobile number"
                  inputProps={{ pattern: "^[0-9]{10}$" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Email Address"
                  name="emailId"
                  value={formData.emailId}
                  onChange={handleChange}
                  type="email"
                  placeholder="Enter email address"
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Enter full address"
                />
              </Grid>
            </Grid>
          </StyledCard>
          {/* Organ Donation Section */}
          <StyledCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "secondary.main", mr: 2 }}>
                <FavoriteIcon />
              </Avatar>
              <Typography
                variant="h6"
                className="poppins-font"
                sx={{ fontWeight: 600 }}
              >
                Organ Donation
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.organs.allOrgans}
                  onChange={handleChange}
                  name="organs.allOrgans"
                  color="primary"
                />
              }
              label="I wish to donate all organs"
              sx={{ mb: 2 }}
            />
            {!formData.organs.allOrgans && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.organs.liver}
                        onChange={handleChange}
                        name="organs.liver"
                      />
                    }
                    label="Liver"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.organs.kidney}
                        onChange={handleChange}
                        name="organs.kidney"
                      />
                    }
                    label="Kidney"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.organs.heart}
                        onChange={handleChange}
                        name="organs.heart"
                      />
                    }
                    label="Heart"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.organs.lungs}
                        onChange={handleChange}
                        name="organs.lungs"
                      />
                    }
                    label="Lungs"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.organs.pancreas}
                        onChange={handleChange}
                        name="organs.pancreas"
                      />
                    }
                    label="Pancreas"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.organs.intestine}
                        onChange={handleChange}
                        name="organs.intestine"
                      />
                    }
                    label="Intestine"
                  />
                </Grid>
              </Grid>
            )}
          </StyledCard>
          {/* Tissue Donation Section */}
          <StyledCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
                <LocalHospitalIcon />
              </Avatar>
              <Typography
                variant="h6"
                className="poppins-font"
                sx={{ fontWeight: 600 }}
              >
                Tissue Donation
              </Typography>
            </Box>
            {!formData.tissues.allTissues && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tissues.cornea}
                        onChange={handleChange}
                        name="tissues.cornea"
                      />
                    }
                    label="Cornea (Eyes)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tissues.skin}
                        onChange={handleChange}
                        name="tissues.skin"
                      />
                    }
                    label="Skin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tissues.bone}
                        onChange={handleChange}
                        name="tissues.bone"
                      />
                    }
                    label="Bone"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tissues.heartValve}
                        onChange={handleChange}
                        name="tissues.heartValve"
                      />
                    }
                    label="Heart Valves"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tissues.bloodVessels}
                        onChange={handleChange}
                        name="tissues.bloodVessels"
                      />
                    }
                    label="Blood Vessels"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tissues.cartilage}
                        onChange={handleChange}
                        name="tissues.cartilage"
                      />
                    }
                    label="Cartilage"
                  />
                </Grid>
              </Grid>
            )}
          </StyledCard>
          {/* Emergency Contact Section */}
          <StyledCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "error.main", mr: 2 }}>
                <PhoneIcon />
              </Avatar>
              <Typography
                variant="h6"
                className="poppins-font"
                sx={{ fontWeight: 600 }}
              >
                Emergency Contact
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Contact Person Name"
                  name="emergencyContact.name"
                  value={formData.emergencyContact.name}
                  onChange={handleChange}
                  placeholder="Enter emergency contact name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Relation</InputLabel>
                  <StyledSelect
                    name="emergencyContact.relation"
                    value={formData.emergencyContact.relation}
                    label="Relation"
                    onChange={handleChange}
                  >
                    <MenuItem value="Father">Father</MenuItem>
                    <MenuItem value="Mother">Mother</MenuItem>
                    <MenuItem value="Spouse">Spouse</MenuItem>
                    <MenuItem value="Son">Son</MenuItem>
                    <MenuItem value="Daughter">Daughter</MenuItem>
                    <MenuItem value="Brother">Brother</MenuItem>
                    <MenuItem value="Sister">Sister</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </StyledSelect>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Contact Number"
                  name="emergencyContact.mobile"
                  value={formData.emergencyContact.mobile}
                  onChange={handleChange}
                  placeholder="Enter contact number"
                  inputProps={{ pattern: "^[0-9]{10}$" }}
                />
              </Grid>
            </Grid>
          </StyledCard>
          {/* Member to Notify Section */}
          <StyledCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "warning.main", mr: 2 }}>
                <PersonIcon />
              </Avatar>
              <Typography
                variant="h6"
                className="poppins-font"
                sx={{ fontWeight: 600 }}
              >
                Member to Notify
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={memberSameAsEmergency}
                  onChange={(e) => setMemberSameAsEmergency(e.target.checked)}
                  name="memberSameAsEmergency"
                  color="primary"
                />
              }
              label="Same as emergency contact"
              sx={{ mb: 2 }}
            />
            {memberSameAsEmergency && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Member Name"
                    name="memberToNotify.name"
                    value={formData.memberToNotify.name}
                    onChange={handleChange}
                    placeholder="Enter member name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Relation</InputLabel>
                    <StyledSelect
                      name="memberToNotify.relation"
                      value={formData.memberToNotify.relation}
                      label="Relation"
                      onChange={handleChange}
                    >
                      <MenuItem value="Father">Father</MenuItem>
                      <MenuItem value="Mother">Mother</MenuItem>
                      <MenuItem value="Spouse">Spouse</MenuItem>
                      <MenuItem value="Son">Son</MenuItem>
                      <MenuItem value="Daughter">Daughter</MenuItem>
                      <MenuItem value="Brother">Brother</MenuItem>
                      <MenuItem value="Sister">Sister</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Contact Number"
                    name="memberToNotify.mobile"
                    value={formData.memberToNotify.mobile}
                    onChange={handleChange}
                    placeholder="Enter contact number"
                    inputProps={{ pattern: "^[0-9]{10}$" }}
                  />
                </Grid>
              </Grid>
            )}
          </StyledCard>
          f{/* Authorization Section */}
          <StyledCard>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                <CakeIcon />
              </Avatar>
              <Typography
                variant="h6"
                className="poppins-font"
                sx={{ fontWeight: 600 }}
              >
                Authorization
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.authorized}
                  onChange={handleChange}
                  name="authorized"
                  color="primary"
                  required
                />
              }
              label="I hereby authorize the donation of my organs and tissues after death for the purpose of transplantation, therapy, medical research, or education as determined by the medical institution."
              sx={{ mb: 2 }}
            />
          </StyledCard>
          {/* Admin Section - Only show if editing */}
          {currentDonorApplication && (
            <StyledCard>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "grey.600", mr: 2 }}>
                  <HomeIcon />
                </Avatar>
                <Typography
                  variant="h6"
                  className="poppins-font"
                  sx={{ fontWeight: 600 }}
                >
                  Admin Panel
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <StyledSelect
                      name="status"
                      value={formData.status}
                      label="Status"
                      onChange={handleChange}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>
              </Grid>
            </StyledCard>
          )}
          {/* Form Actions */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              py: 2,
              mt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              zIndex: 1000,
            }}
          >
            <StyledButton
              onClick={handleCancel}
              variant="outlined"
              sx={{ minWidth: 120 }}
              disabled={loading}
            >
              Cancel
            </StyledButton>
            <StyledButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading
                ? currentDonorApplication
                  ? "Updating..."
                  : "Creating..."
                : currentDonorApplication
                ? "Update"
                : "Create"}
            </StyledButton>
          </Box>
        </form>
      </Box>

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
          autoClose={notification.type === "success"}
          duration={3000}
        />
      )}

      {/* Loader */}
      {loading && <Loader />}
    </StyledFormContainer>
  );
};

export default DonorApplicationForm;
